
import React, {
  useEffect,
  useState,
} from "react";
import styled from "styled-components";

import {
  AdminButton,
} from "../../../pages/admin/action_engine";


export const SUPPLIER_CONTACT_TYPES =
  Object.freeze([
    "SALES",
    "ORDER",
    "ADV",
    "SUPPORT",
    "TECHNICAL",
    "ACCOUNTING",
  ]);


const EMPTY_VALUES = Object.freeze({
  contact_type: "SALES",
  name: "",
  email: "",
  phone: "",
  is_primary: false,
  is_active: true,
  notes: "",
});


function buildValues(contact) {
  if (!contact) {
    return {
      ...EMPTY_VALUES,
    };
  }

  return {
    contact_type:
      contact.contact_type
      || "SALES",

    name:
      contact.name
      || "",

    email:
      contact.email
      || "",

    phone:
      contact.phone
      || "",

    is_primary:
      Boolean(
        contact.is_primary
      ),

    is_active:
      contact.is_active
      !== false,

    notes:
      contact.notes
      || "",
  };
}


function buildPayload(values) {
  return {
    contact_type:
      values.contact_type,

    name:
      values.name.trim(),

    email:
      values.email.trim()
      || null,

    phone:
      values.phone.trim()
      || null,

    is_primary:
      Boolean(
        values.is_primary
      ),

    is_active:
      Boolean(
        values.is_active
      ),

    notes:
      values.notes.trim()
      || null,
  };
}


export default function SupplierContactForm({
  contact = null,
  submitting = false,
  onSubmit,
  onCancel,
}) {
  const [
    values,
    setValues,
  ] = useState(
    () => buildValues(contact),
  );

  const [
    validationError,
    setValidationError,
  ] = useState("");


  useEffect(() => {
    setValues(
      buildValues(contact),
    );

    setValidationError("");

  }, [contact]);


  const handleChange = (
    event,
  ) => {
    const {
      name,
      type,
      checked,
      value,
    } = event.target;

    setValues(
      (current) => ({
        ...current,

        [name]:
          type === "checkbox"
            ? checked
            : value,
      }),
    );
  };


  const handleSubmit = async (
    event,
  ) => {
    event.preventDefault();

    setValidationError("");

    const payload =
      buildPayload(values);

    if (!payload.name) {
      setValidationError(
        "Le nom du contact est requis.",
      );

      return;
    }

    if (
      !payload.email
      && !payload.phone
    ) {
      setValidationError(
        "Un e-mail ou un téléphone "
        + "est requis.",
      );

      return;
    }

    await onSubmit?.(
      payload,
    );
  };


  return (
    <Form
      onSubmit={
        handleSubmit
      }
    >
      <Header>
        <div>
          <Eyebrow>
            Supplier Contact
          </Eyebrow>

          <Title>
            {contact
              ? "Modifier le contact"
              : "Ajouter un contact"}
          </Title>
        </div>
      </Header>


      {validationError && (
        <ValidationError
          role="alert"
        >
          {validationError}
        </ValidationError>
      )}


      <Fields>
        <Field>
          <Label>
            Type
          </Label>

          <select
            name="contact_type"
            aria-label="Type de contact"
            value={
              values.contact_type
            }
            onChange={
              handleChange
            }
            disabled={
              submitting
            }
          >
            {SUPPLIER_CONTACT_TYPES.map(
              (contactType) => (
                <option
                  key={
                    contactType
                  }
                  value={
                    contactType
                  }
                >
                  {contactType}
                </option>
              ),
            )}
          </select>
        </Field>


        <Field>
          <Label>
            Nom
          </Label>

          <input
            name="name"
            aria-label="Nom du contact"
            value={
              values.name
            }
            onChange={
              handleChange
            }
            disabled={
              submitting
            }
          />
        </Field>


        <Field>
          <Label>
            E-mail
          </Label>

          <input
            name="email"
            type="email"
            aria-label="E-mail du contact"
            value={
              values.email
            }
            onChange={
              handleChange
            }
            disabled={
              submitting
            }
          />
        </Field>


        <Field>
          <Label>
            Téléphone
          </Label>

          <input
            name="phone"
            aria-label="Téléphone du contact"
            value={
              values.phone
            }
            onChange={
              handleChange
            }
            disabled={
              submitting
            }
          />
        </Field>


        <CheckField>
          <input
            name="is_primary"
            type="checkbox"
            checked={
              values.is_primary
            }
            onChange={
              handleChange
            }
            disabled={
              submitting
            }
          />

          <span>
            Contact principal
            pour ce rôle
          </span>
        </CheckField>


        <CheckField>
          <input
            name="is_active"
            type="checkbox"
            checked={
              values.is_active
            }
            onChange={
              handleChange
            }
            disabled={
              submitting
            }
          />

          <span>
            Contact actif
          </span>
        </CheckField>


        <NotesField>
          <Label>
            Notes
          </Label>

          <textarea
            name="notes"
            rows={4}
            aria-label="Notes du contact"
            value={
              values.notes
            }
            onChange={
              handleChange
            }
            disabled={
              submitting
            }
          />
        </NotesField>
      </Fields>


      <Actions>
        <AdminButton
          type="button"
          variant="secondary"
          disabled={
            submitting
          }
          onClick={
            onCancel
          }
        >
          Annuler
        </AdminButton>

        <AdminButton
          type="submit"
          variant="primary"
          disabled={
            submitting
          }
        >
          {submitting
            ? "Enregistrement…"
            : "Enregistrer"}
        </AdminButton>
      </Actions>
    </Form>
  );
}


const Form = styled.form`
  display: grid;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.border
      || "#d8dde6"};
  border-radius: 10px;
`;

const Header = styled.header`
  display: flex;
  justify-content:
    space-between;
`;

const Eyebrow = styled.p`
  margin: 0 0 0.25rem;
  font-size: 0.75rem;
  text-transform:
    uppercase;
  opacity: 0.65;
`;

const Title = styled.h4`
  margin: 0;
`;

const ValidationError = styled.div`
  padding: 0.75rem;
  border-radius: 8px;
  background:
    rgba(180, 20, 20, 0.08);
`;

const Fields = styled.div`
  display: grid;
  grid-template-columns:
    repeat(
      auto-fit,
      minmax(220px, 1fr)
    );
  gap: 1rem;
`;

const Field = styled.label`
  display: grid;
  gap: 0.4rem;

  input,
  select,
  textarea {
    width: 100%;
  }
`;

const Label = styled.span`
  font-weight: 600;
`;

const NotesField =
  styled(Field)`
    grid-column: 1 / -1;
  `;

const CheckField =
  styled.label`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `;

const Actions = styled.div`
  display: flex;
  justify-content:
    flex-end;
  gap: 0.75rem;
`;
